/**
 * reset_password_controller.js — set-new-password form behaviour (Stimulus).
 *
 * Scoped to the reset <form>. Handles what the page used to wire with end-of-body
 * getElementById + addEventListener + DOMContentLoaded (none survive a Turbo <body>
 * swap): show/hide toggles for both password fields, a live password-strength
 * meter, the submit guard (match + min-length), and the submit spinner.
 *
 * The form keeps data-turbo="false" — a successful reset 302s into the auth shell
 * (different tracked assets), so submit is intentionally a full navigation; the
 * spinner shows until unload. Validation errors use window.showNotification
 * (showErrorMessage is not loaded on the public shell). Server flash() messages
 * are rendered globally by the shell, so the old inline get_flashed_messages
 * block is gone.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        togglePassword(event) {
            this._toggle(event, '#password', '#togglePasswordIcon');
        }

        toggleConfirmPassword(event) {
            this._toggle(event, '#confirm_password', '#toggleConfirmPasswordIcon');
        }

        updateStrength() {
            var input = this._q('#password');
            var fill = this._q('#strengthFill');
            var text = this._q('#strengthText');
            if (!input || !fill || !text) return;

            var strength = this._calculateStrength(input.value);
            fill.style.width = strength + '%';

            if (strength < 50) {
                fill.style.backgroundColor = '#dc3545';
                text.textContent = 'Password strength: Weak';
                text.className = 'text-danger';
            } else if (strength < 75) {
                fill.style.backgroundColor = '#ffc107';
                text.textContent = 'Password strength: Fair';
                text.className = 'text-warning';
            } else {
                fill.style.backgroundColor = '#198754';
                text.textContent = 'Password strength: Strong';
                text.className = 'text-success';
            }
        }

        onSubmit(event) {
            var passwordInput = this._q('#password');
            var confirmInput = this._q('#confirm_password');
            var password = passwordInput ? passwordInput.value : '';
            var confirmPassword = confirmInput ? confirmInput.value : '';

            if (password !== confirmPassword) {
                if (window.showNotification) window.showNotification('Passwords do not match', 'error');
                event.preventDefault();
                return;
            }

            if (password.length < 8) {
                if (window.showNotification) window.showNotification('Password must be at least 8 characters long', 'error');
                event.preventDefault();
                return;
            }

            var btn = this._q('#updateBtn');
            var spinner = this._q('#updateSpinner');
            if (btn) {
                btn.disabled = true;
                if (spinner) spinner.classList.remove('d-none');
                btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Updating Password...';
            }
        }

        _toggle(event, inputSelector, iconSelector) {
            var input = this._q(inputSelector);
            var icon = this._q(iconSelector);
            var button = event.currentTarget;
            if (!input) return;

            if (input.type === 'password') {
                input.type = 'text';
                if (icon) icon.className = 'bi bi-eye-slash';
                if (button) button.title = 'Hide password';
            } else {
                input.type = 'password';
                if (icon) icon.className = 'bi bi-eye';
                if (button) button.title = 'Show password';
            }
        }

        _calculateStrength(password) {
            var score = 0;
            if (password.length >= 8) score += 25;
            if (password.match(/[a-z]+/)) score += 25;
            if (password.match(/[A-Z]+/)) score += 25;
            if (password.match(/[0-9]+/)) score += 25;
            if (password.match(/[^A-Za-z0-9]+/)) score += 25;
            return Math.min(score, 100);
        }

        _q(selector) { return this.element.querySelector(selector); }
}
